import React from 'react';
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';

const TestimonialsSection = () => {
  const testimonials = [
    {
      name: 'Mahen Asar',
      country: 'Dubai',
      role: 'Off-Plan Investment',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1777189138/Mahen_Asar_Dubai_e3qatq.jpg',
      text: `After hearing mixed stories about off plan properties, I was hesitant to invest until I met Ahaskar from Nava. Their thorough analysis of projected ROI, market demand, and growth potential gave me confidence to move forward. Today, I'm enjoying a stable passive income and can see my property's value increasing. If you're looking for real market insight and an agency that genuinely cares, Nava is the one.`,
    },
    {
      name: 'Yogesh Yadav',
      country: 'Dubai',
      role: 'Real Estate Investor',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1777189139/yogesh_ljppsy.png',
      text: `I used to think investing in Dubai's real estate market was overwhelming, but Nava made it surprisingly accessible. Ahaskar thoroughly explained every aspect of the process, from analyzing market trends to understanding the hidden fees involved. Now, I'm the proud owner of an apartment that not only covers its expenses through rentals but also shows a healthy return on investment. Nava's dedicated service and attention to detail made all the difference.`,
    },
    {
      name: 'Ashish Sinha',
      country: 'USA',
      role: 'Investment Strategy',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1777189139/ashish_USA_oc42uy.png',
      text: `I came to Nava with specific financial goals, and Majd took the time to understand exactly what I needed. They guided me to a property that combines high rental demand with consistent price appreciation. It's rare to find realtors who genuinely listen and tailor their approach, but that's exactly what Nava does. I'm beyond satisfied with the investment strategy they helped me develop.`,
    },
    {
      name: 'Anubhav Dutt',
      country: 'Australia',
      role: 'Prime Location Investment',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1777189139/ABHINAV_AUS_c8m0td.png',
      text: `Working with Ahaskar at Nava was a refreshing experience. I'd spoken with a few agencies before, but none of them showed the genuine care or deep local knowledge that Ahaskar brought to the table. They helped me choose a prime location that's already rising in value, and the steady rental income has exceeded my initial projections. I highly recommend Nava if you want a partner that truly has your best interests at heart.`,
    },
    {
      name: 'Usman Ashfaq',
      country: 'Riyadh',
      role: 'Second Home Investment',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1777189139/USMAN_RIYADH_ae3asw.png',
      text: `When I decided to purchase a second home that could double as an investment, Nava exceeded my expectations. Majd presented detailed market reports, explained the ROI potential in various Dubai neighborhoods, and helped me pick the right unit. My property is already generating reliable rental returns when I'm not staying there myself. Nava's dedication and expertise turned what could have been a complex process into a smooth experience.`,
    },
    {
      name: 'Ashish Sharma',
      country: 'Dubai',
      role: 'Townhouse Investment',
      image: 'https://res.cloudinary.com/dqfcgy1vv/image/upload/q_auto/f_auto/v1777189139/Ashish_Sharma_Dubai_jpchrv.png',
      text: `Real estate transactions can be stressful, but Nava made my search straightforward and even enjoyable. With Ahaskar's in-depth knowledge of Dubai's emerging areas, I found a townhouse that checked all the boxes, strong neighborhood demand, promising appreciation, and a great price point. I owe it all to Nava's personalized approach and comprehensive market insights.`,
    },
  ];

  return (
    <section className="relative py-24 overflow-hidden bg-gradient-to-br from-[#FFF8DC] to-[#F5F5DC]">
      {/* Background effects */}
      <div className="absolute inset-0">
        <motion.div
          className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-yellow-50 rounded-full blur-3xl opacity-40"
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -100, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section header */}
        <motion.div
          className="text-center mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400/20 border border-yellow-500/30 rounded-full mb-6"
            whileHover={{ scale: 1.05 }}
          >
            <Quote className="w-5 h-5 text-yellow-600" />
            <span className="text-yellow-700 font-semibold">Client Success Stories</span>
          </motion.div>
          
          <h2 className="text-5xl md:text-7xl font-bold mb-6">
            <span className="text-gray-800">Trusted By</span>
            <br />
            <span className="bg-gradient-to-r from-yellow-500 via-yellow-600 to-yellow-700 bg-clip-text text-transparent">
              Global Investors
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real stories from real investors who transformed their wealth with us
          </p>
        </motion.div>

        {/* Testimonials grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, scale: 0.8, rotateY: -20 }}
              whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
              viewport={{ once: true }}
              transition={{
                delay: index * 0.1,
                duration: 0.7,
                type: "spring",
                stiffness: 100
              }}
            >
              <motion.div
                className="relative bg-white border border-gray-200 rounded-3xl p-8 hover:border-yellow-400/50 hover:shadow-xl transition-all duration-500 cursor-pointer overflow-hidden h-full"
                whileHover={{ y: -8, scale: 1.02 }}
              >
                {/* Quote icon background */}
                <motion.div
                  className="absolute -top-4 -right-4 opacity-5"
                  animate={{
                    rotate: [0, 10, 0],
                    scale: [1, 1.1, 1],
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <Quote className="w-32 h-32 text-yellow-500" />
                </motion.div>

                {/* Content */}
                <div className="relative z-10 space-y-6">
                  {/* Testimonial text */}
                  <motion.p
                    className="text-gray-700 leading-relaxed text-base"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    "{testimonial.text}"
                  </motion.p>

                  {/* Divider */}
                  <motion.div
                    className="h-px bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    transition={{ delay: index * 0.1 + 0.4, duration: 0.6 }}
                  />

                  {/* Client info */}
                  <motion.div
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 + 0.5 }}
                  >
                    <div className="relative">
                      <motion.img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-14 h-14 rounded-full object-cover border-2 border-yellow-400/40"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      />
                      {/* Pulse ring */}
                      <motion.div
                        className="absolute inset-0 rounded-full border-2 border-yellow-400/40"
                        animate={{
                          scale: [1, 1.3, 1],
                          opacity: [0.5, 0, 0.5],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      />
                    </div>
                    
                    <div>
                      <h4 className="text-gray-800 font-bold text-lg">
                        {testimonial.name}
                      </h4>
                      <p className="text-gray-600 text-sm">
                        {testimonial.country}
                      </p>
                    </div>
                  </motion.div>
                </div>

                {/* Glow effect on hover */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-yellow-400/0 to-yellow-600/0 group-hover:from-yellow-400/5 group-hover:to-yellow-600/5 rounded-3xl transition-all duration-500 pointer-events-none"
                />

                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  style={{
                    background: 'linear-gradient(45deg, transparent 30%, rgba(255, 215, 0, 0.1) 50%, transparent 70%)',
                    backgroundSize: '200% 200%',
                  }}
                  animate={{
                    backgroundPosition: ['0% 0%', '100% 100%'],
                  }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: "linear"
                  }}
                />
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Stats section */}
        <motion.div
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.8 }}
        >
          {[
            { value: '100+', label: 'Happy Clients' },
            { value: '10+', label: 'Countries' },
            { value: '100%', label: 'Satisfaction' },
            { value: '7+', label: 'Years Experience' },
          ].map((stat, index) => (
            <motion.div
              key={index}
              className="text-center p-8 bg-white border border-gray-200 rounded-2xl shadow-md hover:shadow-xl transition-all duration-300"
              whileHover={{ scale: 1.05, y: -5 }}
              transition={{ duration: 0.3 }}
            >
              <motion.div
                className="text-4xl font-black bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent mb-2"
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                transition={{ delay: 0.8 + index * 0.1, type: "spring", stiffness: 200 }}
              >
                {stat.value}
              </motion.div>
              <div className="text-gray-600 font-medium">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
